import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { Icon } from '@phosphor-icons/react';
import { Fragment, type ReactNode, useState } from 'react';

import HorizontalTabs from 'components/HorizontalTabs';
import Navbar from 'components/Navbar';
import Sidebar from 'components/Sidebar';
import NotificationBanner from 'components/VaultBanner';

export default function SidebarLayout({ children }: { children: any }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div>
      <NotificationBanner
        title={''}
        description={
          'Get accredited now to secure continued access to new investments without minimum thresholds.'
        }
        routeToPath={'/invest/accreditation'}
        storeKey="hideAccreditationBanner"
      />
      <Transition show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
          <TransitionChild
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/80" />
          </TransitionChild>

          <div className="fixed inset-0 flex">
            <TransitionChild
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <DialogPanel className="relative mr-16 flex w-full max-w-xs flex-1 ">
                <TransitionChild
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button
                      type="button"
                      className="-m-2.5 p-2.5"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                    </button>
                  </div>
                </TransitionChild>
                <div className="flex w-full flex-col">
                  <Sidebar mobile />
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>

      {/* Static sidebar for desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-9 lg:flex lg:w-64 lg:flex-col sidebar-container">
        <div className="flex w-full h-full">
          <Sidebar />
        </div>
      </div>

      <div className="lg:pl-64">
        <div className="sticky top-0 z-10 lg:mx-auto lg:max-w-8xl lg:px-8 lg:hidden bg-white">
          <div className="flex h-16 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-0 lg:shadow-none">
            <button
              type="button"
              className="-m-2.5 p-2.5 text-gray-700"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="sr-only">Open sidebar</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>

            {/* Separator */}
            <div className="h-6 w-px bg-gray-200" aria-hidden="true" />
          </div>
        </div>

        {children}
      </div>
    </div>
  );
}

export type SidebarLayoutSecondaryTab = {
  key: string;
  label: string;
  description: string;
  route: string;
  icon: Icon | string;
  exact?: boolean;
  badge?: number;
};

export function SidebarLayoutNavigation({
  title,
  subTitle,
  onBackButtonClick,
  secondaryTabs,
  rightContent,
  hideTabHighlight,
}: {
  title: string;
  subTitle: string;
  onBackButtonClick?: () => void;
  secondaryTabs?: SidebarLayoutSecondaryTab[];
  rightContent?: ReactNode;
  hideTabHighlight?: boolean;
}) {
  return (
    <nav className="sticky top-0 z-10 mx-auto max-w-5xl border-b border-slate-100 bg-white pb-2 pl-4 pt-4 shadow-sm lg:-pl-3 mb-8">
      <Navbar title={title || 'title'} subTitle={subTitle} onBackButtonClick={onBackButtonClick} />
      {(secondaryTabs && secondaryTabs.length > 0) || rightContent ? (
        <div className="mt-3 pr-4 lg:pr-3 flex items-center justify-between gap-4">
          {secondaryTabs && secondaryTabs.length > 0 ? (
            <HorizontalTabs tabs={secondaryTabs} disableActiveHighlight={hideTabHighlight} />
          ) : (
            <div />
          )}
          {rightContent ? <div className="shrink-0">{rightContent}</div> : null}
        </div>
      ) : null}
    </nav>
  );
}

export function SidebarLayoutContent({
  children,
  secondaryTabs,
}: {
  children: any;
  secondaryTabs?: SidebarLayoutSecondaryTab[];
}) {
  return (
    <main className="mx-auto max-w-5xl">
      <div>
        <div className="px-4 py-0 lg:py-3 flex flex-col gap-4">
          {secondaryTabs && (
            <nav>
              <HorizontalTabs tabs={secondaryTabs} />
            </nav>
          )}
          {children}
        </div>
      </div>
    </main>
  );
}
